"use client";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { fetchMatchOdds } from "@/app/(app)/actions/fetchMatchOdds";
import type { FUSportsBettingOperatorOdds } from "fansunited-data-layer";
import { useLocale } from "@/lib/labels/LabelsProvider";
import type { LocaleCode } from "@/lib/labels/types";

/**
 * Same `Map<matchId, operators[]>` shape as `useServerBatchMatchOdds` so
 * downstream adapters (`toMatchOddsMap`, etc.) work unchanged.
 */
type MatchOddsBridgeMap = Map<string, FUSportsBettingOperatorOdds[]>;

const DEFAULT_POLLING_INTERVAL_MS = 30000;

export interface UseServerMatchOddsOptions {
    /** Native FansUnited match id (e.g. "fb:m:123456"). Empty string disables the fetch. */
    matchId: string;
    /** Restrict the response to specific markets (e.g. `["1x2"]`). Omit for all. */
    markets?: string[];
    /** Whether the match is live — enables polling when true. */
    isLive?: boolean;
    /** Polling interval in milliseconds (default: 30000). */
    pollingIntervalMs?: number;
    /** Filter odds by a specific bookmaker/operator id (applied client-side). */
    operatorId?: string;
    /** Locale for resolving tenant-specific odds config. */
    locale?: LocaleCode;
}

/**
 * Single-match variant of `useServerBatchMatchOdds`, backed by the newer
 * `getEventOddsByMatchId` SDK endpoint via the `fetchMatchOdds` server action.
 *
 * Returns odds in the same `Map<matchId, operators[]>` shape as the batch
 * hook — with one entry — so callers that already flow through
 * `toMatchOddsMap` keep working unchanged.
 */
export function useServerMatchOdds({
    matchId,
    markets,
    isLive = false,
    pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
    operatorId,
    locale: localeProp,
}: UseServerMatchOddsOptions) {
    const contextLocale = useLocale();
    const locale = localeProp || contextLocale;
    const [oddsMap, setOddsMap] = useState<MatchOddsBridgeMap>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const isInitialLoad = useRef(true);

    // Stable key so the effect only restarts when the market set actually changes.
    const marketsKey = useMemo(() => (markets ? [...markets].sort().join(",") : ""), [markets]);

    const load = useCallback(
        async (isRefresh = false) => {
            if (!matchId) {
                setOddsMap(new Map());
                setLoading(false);
                return;
            }

            if (!isRefresh) setLoading(true);
            setError(null);

            try {
                const result = await fetchMatchOdds({
                    matchId,
                    marketTypes: markets,
                    locale,
                });

                const map: MatchOddsBridgeMap = new Map();
                if (result && result.operators.length > 0) {
                    const operators = operatorId
                        ? result.operators.filter((op) => op.operator.id === operatorId)
                        : result.operators;
                    if (operators.length > 0) map.set(matchId, operators);
                }
                setOddsMap(map);
            } catch (err) {
                console.error("[useServerMatchOdds] Error:", err);
                setError(err instanceof Error ? err : new Error("Failed to fetch match odds"));
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [matchId, marketsKey, operatorId, locale]
    );

    useEffect(() => {
        isInitialLoad.current = true;
        load(false);
    }, [load]);

    useEffect(() => {
        if (!isLive || !matchId) return;
        const intervalId = setInterval(() => load(true), pollingIntervalMs);
        return () => clearInterval(intervalId);
    }, [isLive, matchId, pollingIntervalMs, load]);

    const refetch = useCallback(() => load(true), [load]);

    return { oddsMap, loading, error, refetch };
}
